/**
 * §5.3 — the mobile player interface.
 *
 * §5.3: "The player interface should not contain unnecessary navigation during
 * active gameplay," and the first screen is the join screen — there is no mode
 * selection, because solo play is out of scope (§3.2).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { EventEnvelope, EventPayload } from "@tq/protocol";
import { Avatar, BrandLockup } from "@tq/ui";
import { api, setPlayerToken, type SessionSnapshot } from "../api.js";
import { useRealtime } from "../useRealtime.js";
import { SignInScreen } from "../auth/SignInScreen.js";
import { CodeScreen } from "../auth/CodeScreen.js";
import { fetchAuthState } from "../auth/authApi.js";
import { clearPendingCode, readPendingCode, writePendingCode } from "../auth/pendingCode.js";
import {
  clearPendingAccess,
  readPendingAccess,
  writePendingAccess,
} from "../auth/pendingAccess.js";
import { AccessRequestScreen } from "./AccessRequestScreen.js";
import { JoinScreen } from "./JoinScreen.js";
import { Onboarding } from "./Onboarding.js";
import { EditProfile } from "./EditProfile.js";
import { RankChip, StandingBar } from "./StandingBar.js";
import { WaitingScreen } from "./WaitingScreen.js";
import { AnswerScreen } from "./AnswerScreen.js";
import { PersonalResult } from "./PersonalResult.js";
import { PhoneLeaderboard } from "./PhoneLeaderboard.js";
import { useConfetti } from "../useConfetti.js";

/**
 * §21.6 / F-073 — how far post-reconnect snapshot refetches are spread.
 *
 * Short enough that a player does not notice, wide enough that sixty phones do
 * not ask for a full snapshot in the same millisecond.
 */
const RESYNC_STAGGER_MS = 2000;

type Phase =
  | { kind: "join" }
  /**
   * Arriving from an emailed invitation. The link's token is exchanged for a
   * signed-in session before anything else is shown; there is no code to type
   * and no address to ask for. `error` is set when the link was refused — it
   * expired with the game, was revoked, or was replaced by a newer one.
   */
  | { kind: "invite"; error: string | null }
  /**
   * design §4 — verification sits between the room code and onboarding, not in
   * front of the room code. By this point the screen knows which event is being
   * joined, so signing in reads as part of joining rather than as a gate in
   * front of it. A returning player whose cookie is still valid never sees it.
   */
  | { kind: "sign_in"; sessionCode: string; sessionTitle: string; accessRequests: boolean }
  | {
      kind: "verify";
      sessionCode: string;
      sessionTitle: string;
      email: string;
      /** The session lets a player whose code never comes ask the facilitator. */
      accessRequests: boolean;
    }
  /**
   * Asked the facilitator to be let in, and waiting for the answer. `token` is
   * what collects it; see pendingAccess.ts for why it is also written down.
   */
  | {
      kind: "access";
      sessionCode: string;
      sessionTitle: string;
      email: string;
      name: string;
      token: string;
    }
  | {
      kind: "onboarding";
      sessionCode: string;
      sessionTitle: string;
      /** Null where sign-in is unavailable; the form asks for an address then. */
      verifiedEmail: string | null;
      /** The name an invitation carried, to start the form from. */
      suggestedName?: string | null;
    }
  | { kind: "lobby" }
  | { kind: "question"; payload: EventPayload<"question.opened">; roundToken: string | null }
  | { kind: "result"; payload: EventPayload<"player.result.published"> }
  | { kind: "standings"; payload: EventPayload<"standings.published"> }
  | { kind: "waiting" }
  /** Answering is over and the server is grading. Not the same as waiting. */
  | { kind: "scoring" }
  | { kind: "final" };

export interface PlayerIdentity {
  sessionPlayerId: string;
  displayName: string;
  /**
   * The player's own avatar id — the same one the projector draws them with.
   *
   * Null only until the profile exists. It is carried on the identity rather
   * than read out of `snapshot.players` because the roster a player receives is
   * trimmed under low bandwidth (F-073), and a player's own face is the last
   * thing that should disappear from their own phone.
   */
  photoUrl: string | null;
  /** §36.1 — seeds the edit sheet. The address is shown there, never sent. */
  fullName: string;
  email: string | null;
}

/**
 * Where a phone that reloaded itself should land.
 *
 * Almost always the join screen. The exception is the one moment this flow is
 * fragile: a code has been sent and its owner has left for their mail app. A
 * backgrounded tab is a tab the operating system may discard, and the reload on
 * return would otherwise throw away the only thing that cannot be re-derived —
 * which address the code went to — and ask for it again while the code sits in
 * the inbox, still good.
 *
 * A room code in the URL wins over a remembered one. A player who scanned a
 * second event's QR is joining that event, whatever the last one was; the
 * remembered record is only allowed to fill in a blank, never to redirect.
 */
function restoredPhase(codeFromUrl: string): Phase {
  // A request waiting on the facilitator outranks a code: it is the later of
  // the two steps, and the phone was on its waiting screen when it went away.
  const access = readPendingAccess();
  if (access) {
    if (codeFromUrl && codeFromUrl.toUpperCase() !== access.session.code.toUpperCase()) {
      clearPendingAccess();
    } else {
      return {
        kind: "access",
        sessionCode: access.session.code,
        sessionTitle: access.session.title,
        email: access.email,
        name: access.name,
        token: access.token,
      };
    }
  }

  const pending = readPendingCode();
  if (!pending?.session) return { kind: "join" };
  if (codeFromUrl && codeFromUrl.toUpperCase() !== pending.session.code.toUpperCase()) {
    clearPendingCode();
    return { kind: "join" };
  }
  return {
    kind: "verify",
    sessionCode: pending.session.code,
    sessionTitle: pending.session.title,
    email: pending.email,
    accessRequests: pending.session.accessRequests === true,
  };
}

export function PlayerApp(): React.ReactElement {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const codeFromUrl = params.sessionCode ?? searchParams.get("code") ?? "";
  /**
   * Left in the address bar on purpose. A reload opens the link again, which
   * returns this device to its seat — exactly what a reload should do — and the
   * token was already in plain sight in the email it came from.
   */
  const inviteToken = searchParams.get("invite");

  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [identity, setIdentity] = useState<PlayerIdentity | null>(null);
  /** §36.1 — the edit sheet, open only from the lobby. */
  const [editing, setEditing] = useState(false);
  const [phase, setPhase] = useState<Phase>(() =>
    inviteToken ? { kind: "invite", error: null } : restoredPhase(codeFromUrl),
  );
  const [standing, setStanding] = useState<EventPayload<"player.standing.updated">>({
    totalScore: 0,
    position: null,
    participants: 0,
    pointsToNextRank: null,
    rankDelta: null,
  });
  const [error, setError] = useState<string | null>(null);
  const resultTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The session beat lengths, read inside event handlers without re-binding them. */
  const beatsRef = useRef({ resultsSeconds: 5, standingsSeconds: 10, manual: false });

  /** §26.1 — restore everything on reconnect: profile, score, rank, and state. */
  const refreshState = useCallback(async (sessionId: string) => {
    try {
      const state = await api.playerState(sessionId);
      setSnapshot(state.snapshot);
      setIdentity({
        sessionPlayerId: state.sessionPlayerId,
        displayName: state.displayName,
        photoUrl: state.photoUrl,
        fullName: state.fullName,
        email: state.email,
      });
      setStanding((current) => ({
        ...current,
        totalScore: state.totalScore,
        position: state.position,
        participants: state.participants,
      }));

      const round = state.snapshot.currentRound;
      if (
        round &&
        (round.status === "Reading" || round.status === "Open") &&
        !state.hasSubmittedCurrentRound
      ) {
        setPhase({ kind: "question", payload: round, roundToken: state.roundToken });
      } else if (state.snapshot.status === "Completed") {
        setPhase({ kind: "final" });
      } else if (state.snapshot.status === "Lobby" || state.snapshot.status === "Draft") {
        setPhase({ kind: "lobby" });
      } else {
        // §26.1 — a player who already submitted cannot submit again.
        setPhase({ kind: "waiting" });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not restore your session.");
    }
  }, []);

  const handleEvent = useCallback((event: EventEnvelope) => {
    switch (event.type) {
      case "session.started": {
        setPhase({ kind: "waiting" });
        setSnapshot((current) => (current ? { ...current, status: "Active" } : current));
        break;
      }
      case "question.opened": {
        const payload = event.payload as EventPayload<"question.opened">;
        if (resultTimer.current) clearTimeout(resultTimer.current);
        // The round token arrives with the state refresh; the prompt renders
        // immediately either way, because §8.2 puts it on the device at the
        // start of the reading window.
        setPhase({ kind: "question", payload, roundToken: null });
        break;
      }
      case "question.extended": {
        const payload = event.payload as EventPayload<"question.extended">;
        setPhase((current) =>
          current.kind === "question" && current.payload.roundId === payload.roundId
            ? { ...current, payload: { ...current.payload, closesAt: payload.closesAt } }
            : current,
        );
        break;
      }
      case "question.closed": {
        // Grading and scoring take a moment, so results arrive a few seconds
        // after the close. Dropping to the generic waiting screen for that gap
        // told the player the facilitator had moved on, which looked like their
        // answer had been thrown away. This says what is actually happening.
        setPhase((current) => (current.kind === "question" ? { kind: "scoring" } : current));
        // If the result never lands — a dropped event, a grading failure — do
        // not leave the device promising one forever.
        if (resultTimer.current) clearTimeout(resultTimer.current);
        resultTimer.current = setTimeout(
          () => setPhase((current) => (current.kind === "scoring" ? { kind: "waiting" } : current)),
          15_000,
        );
        break;
      }
      case "player.result.published": {
        const payload = event.payload as EventPayload<"player.result.published">;
        if (resultTimer.current) clearTimeout(resultTimer.current);
        setPhase({ kind: "result", payload });
        // The standings normally take over from here. This is the backstop for
        // when they do not — a cancelled beat, a dropped event — so a finished
        // question never strands the device on its own result.
        //
        // §12.5 — under manual advance there is no "should have happened by
        // now": the beat lasts exactly as long as the facilitator wants it to,
        // and a backstop computed from the durations would pull the result off
        // the player's phone while it is still up on the projector.
        const { resultsSeconds, standingsSeconds, manual } = beatsRef.current;
        if (!manual) {
          resultTimer.current = setTimeout(
            () =>
              setPhase((current) => (current.kind === "result" ? { kind: "waiting" } : current)),
            (resultsSeconds + standingsSeconds + 5) * 1000,
          );
        }
        break;
      }
      case "standings.published": {
        // §12.3 beat 2 — the room and the phone see the standings together, and
        // the phone then returns to its waiting screen on its own so a player
        // is never left staring at a finished question (P-038).
        const payload = event.payload as EventPayload<"standings.published">;
        if (resultTimer.current) clearTimeout(resultTimer.current);
        setPhase((current) =>
          current.kind === "question" ? current : { kind: "standings", payload },
        );
        // §12.5 — a manually advanced beat leaves on the facilitator's next
        // click, which arrives as `board.updated`, not on a timer.
        if (!payload.manualAdvance) {
          resultTimer.current = setTimeout(
            () =>
              setPhase((current) => (current.kind === "standings" ? { kind: "waiting" } : current)),
            payload.beatDurationMs,
          );
        }
        break;
      }
      case "player.standing.updated": {
        // §5.4 — the standing the device shows on every screen.
        setStanding(event.payload as EventPayload<"player.standing.updated">);
        break;
      }
      case "score.corrected": {
        const payload = event.payload as EventPayload<"score.corrected">;
        setStanding((current) => ({ ...current, totalScore: payload.totalScore }));
        break;
      }
      case "board.updated": {
        // P-038 — the client moves from results back to waiting without a refresh.
        setPhase((current) =>
          current.kind === "result" || current.kind === "standings" || current.kind === "waiting"
            ? { kind: "waiting" }
            : current,
        );
        break;
      }
      case "question.cancelled": {
        setPhase({ kind: "waiting" });
        break;
      }
      // F-073 — the facilitator can flip this while the session is running. A
      // small event rather than a snapshot refetch, which is exactly the burst
      // low-bandwidth mode exists to avoid.
      case "session.settings.updated": {
        const payload = event.payload as EventPayload<"session.settings.updated">;
        setSnapshot((current) =>
          current
            ? {
                ...current,
                settings: {
                  ...current.settings,
                  lowBandwidthMode: payload.lowBandwidthMode,
                  manualBeatAdvance: payload.manualBeatAdvance,
                },
              }
            : current,
        );
        break;
      }
      case "session.paused": {
        setSnapshot((current) => (current ? { ...current, status: "Paused" } : current));
        break;
      }
      case "session.resumed": {
        setSnapshot((current) => (current ? { ...current, status: "Active" } : current));
        break;
      }
      case "session.completed": {
        setSnapshot((current) => (current ? { ...current, status: "Completed" } : current));
        setPhase({ kind: "final" });
        break;
      }
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (!snapshot) return;
    beatsRef.current = {
      resultsSeconds: snapshot.settings.questionResultsSeconds,
      standingsSeconds: snapshot.settings.overallStandingsSeconds,
      manual: snapshot.settings.manualBeatAdvance,
    };
  }, [
    snapshot?.settings.questionResultsSeconds,
    snapshot?.settings.overallStandingsSeconds,
    snapshot?.settings.manualBeatAdvance,
  ]);

  const { state, clockOffsetMs } = useRealtime(snapshot, handleEvent);

  // §14.6 — the celebration lands on the phone too, once the game is over.
  // F-073 — ninety animated nodes per side is CPU an old phone on a struggling
  // network can do without; under low bandwidth the celebration is skipped.
  useConfetti(phase.kind === "final" && !snapshot?.settings.lowBandwidthMode);

  // §26.1 — after a reconnection, resynchronize with the server rather than
  // trusting whatever the screen was showing when the connection dropped.
  //
  // §21.6 / F-073 — spread over a short window rather than fired immediately.
  // An access-point blip reconnects every phone in the room at once, and each
  // one asking for a full snapshot in the same instant is a second burst on a
  // network that has just proven it cannot take one.
  const wasConnected = useRef(false);
  useEffect(() => {
    if (state === "connected" && wasConnected.current && snapshot) {
      const sessionId = snapshot.sessionId;
      const delayMs = Math.random() * RESYNC_STAGGER_MS;
      const timer = setTimeout(() => void refreshState(sessionId), delayMs);
      wasConnected.current = true;
      return () => clearTimeout(timer);
    }
    wasConnected.current = state === "connected";
    return undefined;
  }, [state, snapshot?.sessionId, refreshState]);

  useEffect(() => {
    return () => {
      if (resultTimer.current) clearTimeout(resultTimer.current);
    };
  }, []);

  const handleJoin = useCallback(
    async (code: string) => {
      setError(null);
      const result = await api.join(code);
      if (result.status === "onboarding_required") {
        // Ask the server whether this deployment can verify anyone at all. In
        // Local Event Mode it cannot — a venue LAN has no route to email — and
        // putting a sign-in screen there would be a dead end.
        const auth = await fetchAuthState();
        const needsSignIn = auth.identityMode === "verified" && !auth.signedIn;
        setPhase(
          needsSignIn
            ? {
                kind: "sign_in",
                sessionCode: result.sessionCode,
                sessionTitle: result.sessionTitle,
                accessRequests: result.accessRequests === true,
              }
            : {
                kind: "onboarding",
                sessionCode: result.sessionCode,
                sessionTitle: result.sessionTitle,
                // A returning player arrives here already signed in.
                verifiedEmail: auth.email ?? null,
              },
        );
        return;
      }
      setSnapshot(result.snapshot);
      setIdentity({
        sessionPlayerId: result.sessionPlayerId,
        displayName: result.displayName,
        photoUrl: result.photoUrl,
        // This rejoin response does not carry them; `refreshState` below is the
        // canonical read and fills both in a moment later. The edit sheet needs
        // a snapshot in Lobby to open at all, which the same call supplies.
        fullName: "",
        email: null,
      });
      setPhase(isPreGame(result.snapshot.status) ? { kind: "lobby" } : { kind: "waiting" });
      await refreshState(result.snapshot.sessionId);
    },
    [refreshState],
  );

  /**
   * The invitation link, opened. Runs once per page load: the exchange signs
   * this browser in, and either returns it to the seat the invitation already
   * holds or starts onboarding with the address settled and the name filled in.
   */
  const redeemed = useRef(false);
  useEffect(() => {
    if (!inviteToken || redeemed.current) return;
    redeemed.current = true;
    clearPendingCode();
    clearPendingAccess();
    void (async () => {
      try {
        const invite = await api.redeemInvite(inviteToken);
        if (invite.status === "seated" && invite.profileToken) {
          setPlayerToken(invite.profileToken);
          await handleJoin(invite.sessionCode);
          return;
        }
        setPhase({
          kind: "onboarding",
          sessionCode: invite.sessionCode,
          sessionTitle: invite.sessionTitle,
          verifiedEmail: invite.email,
          suggestedName: invite.suggestedName,
        });
      } catch (cause) {
        setPhase({
          kind: "invite",
          error:
            cause instanceof Error ? cause.message : "This invitation link could not be opened.",
        });
      }
    })();
  }, [inviteToken, handleJoin]);

  const handleOnboarded = useCallback(
    (token: string, next: SessionSnapshot, player: PlayerIdentity) => {
      // §15.1 — the opaque profile token is stored so the same device skips
      // onboarding next time.
      setPlayerToken(token);
      setSnapshot(next);
      setIdentity(player);
      setPhase(isPreGame(next.status) ? { kind: "lobby" } : { kind: "waiting" });
      // §5.4 — the standing is shown on every screen from the moment the player
      // enters, so the participant count is fetched rather than left at zero
      // until the first question is scored.
      void refreshState(next.sessionId);
    },
    [refreshState],
  );

  const handleSubmitted = useCallback(() => {
    setPhase({ kind: "waiting" });
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  if (phase.kind === "invite") {
    return phase.error ? (
      <div className="tq-player">
        <div className="tq-player-main">
          <WaitingScreen icon="✉️" title="This link didn't work" message={phase.error} />
          {codeFromUrl ? (
            <p style={{ textAlign: "center" }}>
              <a href={`/join/${encodeURIComponent(codeFromUrl)}`}>
                Join with the game code instead
              </a>
            </p>
          ) : null}
        </div>
      </div>
    ) : (
      <div className="tq-player">
        <div className="tq-player-main">
          <WaitingScreen icon="✉️" title="Opening your invitation" message="Signing you in…" />
        </div>
      </div>
    );
  }

  if (phase.kind === "join") {
    return <JoinScreen initialCode={codeFromUrl} onJoin={handleJoin} />;
  }

  if (phase.kind === "sign_in") {
    return (
      <SignInScreen
        heading="JOINING"
        // Naming the event is the point of putting this screen here rather than
        // in front of the room code.
        subheading={phase.sessionTitle}
        onSent={(email) => {
          // Before the trip to the inbox: a phone may reload this tab while its
          // owner is away, and this is what brings them back to the code screen
          // instead of to an empty email field. See pendingCode.ts.
          writePendingCode(email, {
            code: phase.sessionCode,
            title: phase.sessionTitle,
            accessRequests: phase.accessRequests,
          });
          setPhase({
            kind: "verify",
            sessionCode: phase.sessionCode,
            sessionTitle: phase.sessionTitle,
            email,
            accessRequests: phase.accessRequests,
          });
        }}
      />
    );
  }

  if (phase.kind === "verify") {
    return (
      <CodeScreen
        email={phase.email}
        onVerified={() => {
          clearPendingCode();
          setPhase({
            kind: "onboarding",
            sessionCode: phase.sessionCode,
            sessionTitle: phase.sessionTitle,
            verifiedEmail: phase.email,
          });
        }}
        onBack={() => {
          clearPendingCode();
          setPhase({
            kind: "sign_in",
            sessionCode: phase.sessionCode,
            sessionTitle: phase.sessionTitle,
            accessRequests: phase.accessRequests,
          });
        }}
        onRequestAccess={
          phase.accessRequests
            ? async (name) => {
                const { token } = await api.requestAccess({
                  sessionCode: phase.sessionCode,
                  email: phase.email,
                  name,
                });
                clearPendingCode();
                writePendingAccess({
                  token,
                  email: phase.email,
                  name,
                  session: { code: phase.sessionCode, title: phase.sessionTitle },
                });
                setPhase({
                  kind: "access",
                  sessionCode: phase.sessionCode,
                  sessionTitle: phase.sessionTitle,
                  email: phase.email,
                  name,
                  token,
                });
              }
            : undefined
        }
      />
    );
  }

  if (phase.kind === "access") {
    return (
      <AccessRequestScreen
        token={phase.token}
        email={phase.email}
        name={phase.name}
        sessionCode={phase.sessionCode}
        sessionTitle={phase.sessionTitle}
        onApproved={(approved) => {
          // Signed in by the poll that found the approval. From here it is the
          // same road a verified code takes.
          clearPendingAccess();
          setPhase({
            kind: "onboarding",
            sessionCode: approved.sessionCode,
            sessionTitle: approved.sessionTitle,
            verifiedEmail: approved.email,
            suggestedName: approved.suggestedName,
          });
        }}
        onAskAgain={async () => {
          const { token } = await api.requestAccess({
            sessionCode: phase.sessionCode,
            email: phase.email,
            name: phase.name,
          });
          writePendingAccess({
            token,
            email: phase.email,
            name: phase.name,
            session: { code: phase.sessionCode, title: phase.sessionTitle },
          });
          setPhase({ ...phase, token });
        }}
        onUseCode={() => {
          clearPendingAccess();
          writePendingCode(phase.email, {
            code: phase.sessionCode,
            title: phase.sessionTitle,
            accessRequests: true,
          });
          setPhase({
            kind: "verify",
            sessionCode: phase.sessionCode,
            sessionTitle: phase.sessionTitle,
            email: phase.email,
            accessRequests: true,
          });
        }}
        onStartOver={() => {
          clearPendingAccess();
          setPhase({ kind: "join" });
        }}
      />
    );
  }

  if (phase.kind === "onboarding") {
    return (
      <Onboarding
        sessionCode={phase.sessionCode}
        sessionTitle={phase.sessionTitle}
        verifiedEmail={phase.verifiedEmail}
        suggestedName={phase.suggestedName ?? null}
        onComplete={handleOnboarded}
      />
    );
  }

  const paused = snapshot?.status === "Paused";
  const brand = snapshot?.brand ?? { top: "FPPTA", main: "TRUSTEE", accent: "SHOWDOWN" };
  const dotClass =
    state === "connected"
      ? "tq-phone-dot"
      : state === "disconnected"
        ? "tq-phone-dot tq-phone-dot--offline"
        : "tq-phone-dot tq-phone-dot--waiting";

  // §36.1 — only in the lobby, and only once there is an identity to edit.
  const canEditProfile = Boolean(identity) && snapshot?.status === "Lobby";

  return (
    <div className="tq-player">
      {/* The same lockup the projector carries, so the phone reads as the same
          game the room is watching. */}
      <header className="tq-phone-head">
        <BrandLockup brand={brand} />
        {/*
          §36.1 — the corner is where you change your name and picture.

          In the lobby the chip is a button; once the game starts it is the
          plain text it has always been. Editability is expressed as what is
          tappable rather than as an error after the fact, so a player is never
          offered an edit the server will refuse — the server refuses it too,
          which is what actually holds the rule.
        */}
        {canEditProfile ? (
          <button
            type="button"
            className="tq-phone-player tq-phone-player--editable"
            onClick={() => setEditing(true)}
            aria-label={`Edit your name and picture. You are ${identity?.displayName ?? "Player"}.`}
          >
            <span className={dotClass} title={state} aria-hidden="true" />
            <span className="tq-visually-hidden">Connection: {state}. </span>
            {identity && (
              <Avatar photoUrl={identity.photoUrl} displayName={identity.displayName} size={26} />
            )}
            {identity?.displayName ?? "Player"}
            <span aria-hidden="true" style={{ opacity: 0.65, fontSize: 12 }}>
              ✎
            </span>
          </button>
        ) : (
          <span className="tq-phone-player">
            {/* §29.3 — connection state is always visible, and never color alone:
              the title says it in words for anyone who cannot see the dot. */}
            <span className={dotClass} title={state} aria-hidden="true" />
            <span className="tq-visually-hidden">Connection: {state}. </span>
            {/* The avatar a player chose during onboarding, on the screen they
              spend the whole game looking at. It sits to the left of the name
              so the pairing reads the same way round as it does on the
              projector and in the leaderboard rows. */}
            {identity && (
              <Avatar photoUrl={identity.photoUrl} displayName={identity.displayName} size={26} />
            )}
            {identity?.displayName ?? "Player"}
          </span>
        )}
      </header>

      {editing && identity && snapshot && (
        <EditProfile
          sessionId={snapshot.sessionId}
          fullName={identity.fullName}
          email={identity.email}
          onCancel={() => setEditing(false)}
          onDone={(next) => {
            // Applied from the response so the corner changes at once, rather
            // than waiting for the lobby broadcast to come back around.
            setIdentity((current) =>
              current
                ? { ...current, displayName: next.displayName, photoUrl: next.photoUrl }
                : current,
            );
            setEditing(false);
          }}
        />
      )}

      {error && (
        <div role="alert" className="tq-alert" style={{ margin: "0 0 12px" }}>
          {error}
        </div>
      )}

      <div className="tq-player-main">
        {phase.kind === "question" ? (
          <AnswerScreen
            key={phase.payload.roundId}
            payload={phase.payload}
            roundToken={phase.roundToken}
            sessionId={snapshot?.sessionId ?? ""}
            clockOffsetMs={clockOffsetMs}
            connectionState={state}
            onSubmitted={handleSubmitted}
            onError={setError}
          />
        ) : phase.kind === "result" ? (
          <PersonalResult payload={phase.payload} />
        ) : phase.kind === "standings" ? (
          <PhoneLeaderboard
            payload={phase.payload}
            sessionPlayerId={identity?.sessionPlayerId ?? null}
            standing={standing}
          />
        ) : phase.kind === "final" ? (
          <div className="tq-phone-idle">
            <div className="tq-phone-summary-title">🏁 Session Complete!</div>
            <div className="tq-phone-summary-score">{standing.totalScore.toLocaleString()}</div>
            <div>
              <RankChip position={standing.position} participants={standing.participants} />
            </div>
            <p className="tq-phone-summary-sub">
              Thanks for playing, {identity?.displayName ?? "trustee"}!
              <br />
              {standing.position === null
                ? "That's your total score for this session."
                : `You finished ${ordinalOf(standing.position)} out of ${standing.participants} players.`}
            </p>
          </div>
        ) : phase.kind === "lobby" ? (
          <WaitingScreen
            icon="✅"
            title="You're in"
            message={
              snapshot?.status === "Draft"
                ? "You're all set up early. The game hasn't opened yet — keep this screen open, or come back to your invitation link when it's time."
                : "Waiting for the facilitator to start the game. Keep this screen open."
            }
          />
        ) : phase.kind === "scoring" ? (
          <WaitingScreen
            icon="⏳"
            title="Answer locked in"
            message="Scoring this question — your result is coming right up…"
          />
        ) : (
          <WaitingScreen
            title="Get set"
            message="Waiting for the facilitator to open the next question…"
          />
        )}
      </div>

      {/* §5.4 — the standing is on every screen of an active session. */}
      <div className="tq-player-footer">
        <StandingBar standing={standing} />
      </div>

      {/* §5.4 / F-053 — a paused game says so, over everything. */}
      {paused && (
        <div className="tq-phone-paused" role="status">
          <div className="tq-phone-paused-big">⏸ PAUSED</div>
          <div className="tq-phone-paused-sub">The facilitator has paused the game…</div>
        </div>
      )}
    </div>
  );
}

/**
 * Before the first question: the lobby, or — for someone invited who set up
 * early — the Draft session the lobby has not been opened on yet.
 */
function isPreGame(status: string): boolean {
  return status === "Lobby" || status === "Draft";
}

function ordinalOf(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
